import { Bell, Calendar, Plus, ShoppingBag, UserCircle } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import {
  DASHBOARD_PERIODS,
  DASHBOARD_PERIOD_LABELS,
  type DashboardPeriod,
} from '@/services/dashboardApi'
import { useVerticalUI } from '@/lib/verticalUI/useVerticalUI'
import { cn } from '@/lib/utils'

export interface DashboardHeaderProps {
  displayName: string
  unreadCount: number
  period?: DashboardPeriod
  onPeriodChange?: (period: DashboardPeriod) => void
}

/** Encabezado del dashboard: identidad, período, acción principal, alta de canal y bandeja. */
export function DashboardHeader({
  displayName,
  unreadCount,
  period = 'today',
  onPeriodChange,
}: DashboardHeaderProps) {
  const navigate = useNavigate()
  const { profile } = useVerticalUI()
  const unreadLabel = unreadCount > 0
    ? `${unreadCount} conversaciones sin leer`
    : 'Sin conversaciones sin leer'

  return (
    <div className="rounded-xl border border-slate-100 bg-white px-4 pb-4 pt-5 shadow-sm">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand/20 bg-brand/10 text-brand">
              <UserCircle className="h-7 w-7" aria-hidden="true" />
            </div>
            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" aria-hidden="true" />
          </div>
          <div>
            {profile.label && (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                {profile.label}
              </span>
            )}
            <h1 className="text-xl font-bold text-slate-900">Hola, {displayName}</h1>
          </div>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          <div
            role="group"
            aria-label="Período de las métricas"
            className="order-last flex w-full rounded-lg border border-slate-200 bg-slate-50 p-0.5 sm:order-none sm:w-auto"
          >
            {DASHBOARD_PERIODS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={period === option}
                onClick={() => onPeriodChange?.(option)}
                className={cn(
                  'flex-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:flex-none',
                  period === option
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                )}
              >
                {DASHBOARD_PERIOD_LABELS[option]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => navigate(profile.nav.primaryTransactionPath)}
            className="hidden items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 transition-colors hover:bg-emerald-100 sm:flex"
          >
            {profile.operational.hasAppointmentsCalendar ? (
              <Calendar className="h-4 w-4 text-emerald-700" aria-hidden="true" />
            ) : (
              <ShoppingBag className="h-4 w-4 text-emerald-700" aria-hidden="true" />
            )}
            <span>{profile.terminology.primaryTransactionPlural}</span>
          </button>
          <Link
            to="/app/instances?create=true"
            className="hidden items-center gap-2 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-dark sm:flex"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            <span>Agregar Canal</span>
          </Link>
          <button
            type="button"
            onClick={() => navigate('/app/conversations?hasUnread=true')}
            aria-label={unreadLabel}
            className="relative ml-auto rounded-full bg-slate-100 p-2.5 text-slate-600 transition-colors hover:bg-slate-200"
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-red-500 px-0.5 text-xs font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
